import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  convertWeight,
  convertWeightToAll,
  bmiCategory,
  WEIGHT_UNITS,
  type WeightUnitKey,
} from "@/lib/calculators/kgToPounds";

type B = { en: string; bn: string };
type Lang = "en" | "bn";

const T = {
  h1: { en: "KG to Pounds Converter", bn: "কেজি থেকে পাউন্ড কনভার্টার" },
  desc: {
    en: "Convert kg to pounds and back instantly. Plus gram, tola, sher, mon (maund), quintal & ton — Bangladesh weight units in one place.",
    bn: "কেজি থেকে পাউন্ড ও বিপরীতে তাৎক্ষণিক রূপান্তর। গ্রাম, তোলা, সের, মন, কুইন্টাল ও টন — বাংলাদেশের সব ওজনের একক একসাথে।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  amount: { en: "Amount", bn: "পরিমাণ" },
  from: { en: "From", bn: "থেকে" },
  to: { en: "To", bn: "তে" },
  swap: { en: "Swap", bn: "বদল" },
  result: { en: "Result", bn: "ফলাফল" },
  allUnits: { en: "All units", bn: "সব এককে" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "How many pounds in 1 kg?", bn: "১ কেজিতে কত পাউন্ড?" },
    a: { en: "1 kilogram = 2.20462 pounds. So 50 kg × 2.20462 = 110.23 lb.",
         bn: "১ কিলোগ্রাম = ২.২০৪৬২ পাউন্ড। ৫০ কেজি × ২.২০৪৬২ = ১১০.২৩ পাউন্ড।" } },
  { q: { en: "What is 1 mon in kg?", bn: "১ মন কত কেজি?" },
    a: { en: "1 mon (maund) = 40 sher = 37.324 kg = 82.27 lb. It's the standard agricultural weight unit in Bangladesh — used for rice, jute, fish, mustard, etc.",
         bn: "১ মন = ৪০ সের = ৩৭.৩২৪ কেজি = ৮২.২৭ পাউন্ড। বাংলাদেশের কৃষি বাজারে চাল, পাট, মাছ, সরিষায় ব্যবহৃত।" } },
  { q: { en: "১ মন কত কেজি?", bn: "১ মন কত কেজি?" },
    a: { en: "১ মন = ৩৭.৩২৪ কেজি (৪০ সের)।", bn: "১ মন = ৩৭.৩২৪ কেজি (৪০ সের)।" } },
  { q: { en: "১ সের কত গ্রাম?", bn: "১ সের কত গ্রাম?" },
    a: { en: "১ সের ≈ ৯৩৩ গ্রাম (০.৯৩৩১ কেজি)। ১ মনের ১/৪০ অংশ।",
         bn: "১ সের ≈ ৯৩৩ গ্রাম (০.৯৩৩১ কেজি)। ১ মনের ১/৪০ অংশ।" } },
  { q: { en: "১ তোলা সোনা কত গ্রাম?", bn: "১ তোলা সোনা কত গ্রাম?" },
    a: { en: "১ তোলা = ১১.৬৬৪ গ্রাম। বাংলাদেশের জুয়েলারিতে সোনা-রূপার ওজন তোলায় হিসাব হয় (১ ভরি = ১ তোলা)।",
         bn: "১ তোলা = ১১.৬৬৪ গ্রাম। বাংলাদেশের জুয়েলারিতে সোনা-রূপার ওজন তোলায় হিসাব হয় (১ ভরি = ১ তোলা)।" } },
  { q: { en: "৫০ কেজি কত পাউন্ড?", bn: "৫০ কেজি কত পাউন্ড?" },
    a: { en: "৫০ কেজি = ১১০.২৩ পাউন্ড।", bn: "৫০ কেজি = ১১০.২৩ পাউন্ড।" } },
  { q: { en: "How many kg in 100 pounds?", bn: "১০০ পাউন্ড কত কেজি?" },
    a: { en: "100 lb = 100 × 0.453592 = 45.36 kg.",
         bn: "১০০ পাউন্ড = ১০০ × ০.৪৫৩৫৯২ = ৪৫.৩৬ কেজি।" } },
  { q: { en: "How much is 1 mon of rice?", bn: "১ মন চাল কত কেজি?" },
    a: { en: "1 mon of rice = 37.32 kg, regardless of variety. The mon is a weight unit, not a volume unit.",
         bn: "১ মন চাল = ৩৭.৩২ কেজি; চালের জাত বদলালেও মনের ওজন একই।" } },
];

export const Route = createFileRoute("/kg-to-pounds")({
  head: () => {
    const seo = buildSeo({
      title: "KG to Pounds Converter — Lb, Mon, Sher",
      description:
        "Free kg to pounds converter. Convert kg, lb, mon, sher, tola, quintal. Bangladesh weight conversions including traditional units. কেজি থেকে পাউন্ড কনভার্টার।",
      path: "/kg-to-pounds",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "KG to Pounds", path: "/kg-to-pounds" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: KgToPoundsPage,
});

const QUICK_KG = [1, 5, 10, 25, 50, 75, 100];

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function KgToPoundsPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];

  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 4) => {
    if (!Number.isFinite(n)) return "—";
    const rounded = Math.round(n * 10 ** d) / 10 ** d;
    const s = rounded.toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };

  const [from, setFrom] = useState<WeightUnitKey>("kg");
  const [to, setTo] = useState<WeightUnitKey>("lb");
  const [amountStr, setAmountStr] = useState("50");

  const amount = parseFloat(amountStr) || 0;
  const result = useMemo(() => convertWeight(amount, from, to), [amount, from, to]);
  const all = useMemo(() => convertWeightToAll(amount, from), [amount, from]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const fromUnit = WEIGHT_UNITS.find((u) => u.key === from)!;
  const toUnit = WEIGHT_UNITS.find((u) => u.key === to)!;

  // BMI hint: only when input is in body-weight range (kg between 30 and 250)
  const kgEquivalent = amount * fromUnit.kg;
  const bmi = kgEquivalent >= 30 && kgEquivalent <= 250 ? bmiCategory(kgEquivalent) : null;

  const bmiLabel: Record<string, B> = {
    underweight: { en: "Underweight", bn: "কম ওজন" },
    normal: { en: "Normal", bn: "স্বাভাবিক" },
    overweight: { en: "Overweight", bn: "অতিরিক্ত ওজন" },
    obese: { en: "Obese", bn: "স্থূল" },
  };
  const bmiTone: Record<string, string> = {
    underweight: "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300",
    normal: "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
    overweight: "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300",
    obese: "border-red-500/40 bg-red-500/5 text-red-700 dark:text-red-300",
  };

  return (
    <CalculatorLayout
      slug="kg-to-pounds"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Scale"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Direction quick toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => { setFrom("kg"); setTo("lb"); }}
          className={cn(
            "rounded-full border px-4 py-1.5 text-xs font-medium transition-colors",
            from === "kg" && to === "lb" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground",
            lang === "bn" && "font-bn",
          )}
        >
          {lang === "en" ? "KG → Pounds" : "কেজি → পাউন্ড"}
        </button>
        <button
          type="button"
          onClick={() => { setFrom("lb"); setTo("kg"); }}
          className={cn(
            "rounded-full border px-4 py-1.5 text-xs font-medium transition-colors",
            from === "lb" && to === "kg" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground",
            lang === "bn" && "font-bn",
          )}
        >
          {lang === "en" ? "Pounds → KG" : "পাউন্ড → কেজি"}
        </button>
      </div>

      {/* Inputs */}
      <div className="mt-4 grid items-end gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{tr("amount")}</label>
          <input
            type="text"
            inputMode="decimal"
            value={useBn ? toBengaliNumerals(amountStr) : amountStr}
            onChange={(e) => setAmountStr(stripBn(e.target.value))}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-2xl font-semibold tabular-nums focus:border-accent focus:outline-none"
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{tr("from")}</label>
          <UnitSelect value={from} onChange={setFrom} lang={lang} />
        </div>

        <div className="flex items-center justify-center pb-2">
          <button
            type="button"
            onClick={swap}
            aria-label={tr("swap")}
            className="rounded-full border border-border bg-card p-2 hover:border-accent"
          >
            <ArrowLeftRight size={16} />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{tr("to")}</label>
          <UnitSelect value={to} onChange={setTo} lang={lang} />
        </div>

        <div />
      </div>

      {/* Quick chips */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Quick:" : "দ্রুত:"}
        </span>
        {QUICK_KG.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => { setAmountStr(String(n)); setFrom("kg"); }}
            className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-accent"
          >
            {num(n)} {lang === "en" ? "kg" : "কেজি"}
          </button>
        ))}
      </div>

      {/* Primary result */}
      <motion.div
        key={`${from}-${to}-${amountStr}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center"
      >
        <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("result")}
        </p>
        <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
          {fmt(result, 4)}{" "}
          <span className="text-base font-normal text-muted-foreground">
            {lang === "en" ? toUnit.shortEn : toUnit.shortBn}
          </span>
        </p>
        <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
          {fmt(amount, 4)} {lang === "en" ? fromUnit.en : fromUnit.bn} ={" "}
          {fmt(result, 6)} {lang === "en" ? toUnit.en : toUnit.bn}
        </p>
      </motion.div>

      {/* BMI hint */}
      {bmi && (
        <div className={cn("mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 text-sm", bmiTone[bmi.category])}>
          <div className={cn(lang === "bn" && "font-bn")}>
            {lang === "en"
              ? <>If this is your body weight (assumes 1.65 m height): <strong>BMI {fmt(bmi.bmi, 1)} — {bmiLabel[bmi.category].en}</strong>.</>
              : <>এটি যদি আপনার শরীরের ওজন হয় (ধরা হয়েছে উচ্চতা ১.৬৫ মিটার): <strong>BMI {fmt(bmi.bmi, 1)} — {bmiLabel[bmi.category].bn}</strong>।</>}
          </div>
          <Link to="/bmi-calculator" className="rounded-md border border-current/30 px-3 py-1 text-xs hover:bg-current/10">
            {lang === "en" ? "Open BMI Calculator →" : "BMI ক্যালকুলেটর খুলুন →"}
          </Link>
        </div>
      )}

      {/* All units table */}
      <section className="mt-8 space-y-3">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>{tr("allUnits")}</h2>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Value" : "মান"}</th>
                <th className="p-2 text-left">{lang === "en" ? "Note" : "নোট"}</th>
              </tr>
            </thead>
            <tbody>
              {all.map(({ unit, value }) => (
                <tr key={unit.key} className={cn("border-t border-border", unit.key === to && "bg-accent/5")}>
                  <td className="p-2">
                    <span className="font-medium">{lang === "en" ? unit.en : unit.bn}</span>{" "}
                    <span className="text-xs text-muted-foreground">({lang === "en" ? unit.shortEn : unit.shortBn})</span>
                  </td>
                  <td className="p-2 text-right tabular-nums">{fmt(value, value < 1 ? 6 : 4)}</td>
                  <td className={cn("p-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                    {unit.note ? (lang === "en" ? unit.note.en : unit.note.bn) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "KG to pound formula" : "কেজি থেকে পাউন্ডে সূত্র"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "1 kg = 2.20462 lb." : "১ কেজি = ২.২০৪৬২ পাউন্ড।"}</li>
            <li>{lang === "en" ? "1 lb = 0.453592 kg (exact, since 1959)." : "১ পাউন্ড = ০.৪৫৩৫৯২ কেজি (১৯৫৯ থেকে সঠিক)।"}</li>
            <li>{lang === "en" ? "Pounds = kg × 2.20462." : "পাউন্ড = কেজি × ২.২০৪৬২।"}</li>
            <li>{lang === "en" ? "Kg = pounds × 0.453592." : "কেজি = পাউন্ড × ০.৪৫৩৫৯২।"}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Mon (Maund) — Bangladesh's traditional weight" : "মন — বাংলাদেশের ঐতিহ্যবাহী ওজন"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 mon = 40 sher = 37.324 kg ≈ 82.27 lb. The mon is the standard wholesale weight unit across Bangladesh — rice, paddy, jute, mustard, fish, and most agricultural produce are still bought and sold by the mon. Farmers report yields per bigha in mons."
              : "১ মন = ৪০ সের = ৩৭.৩২৪ কেজি ≈ ৮২.২৭ পাউন্ড। মন বাংলাদেশের পাইকারি বাজারের প্রধান ওজন — চাল, ধান, পাট, সরিষা, মাছ ও কৃষিপণ্য মনে কেনাবেচা হয়। কৃষকরা প্রতি বিঘায় ফলন মনে হিসাব করেন।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Sher — the older retail unit" : "সের — পুরনো বাজার একক"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 sher ≈ 933 g (0.9331 kg). Forty sher make 1 mon. Older shops and rural markets still sell oil, sugar, and dal in sher — slightly less than 1 kg, so don't assume 1:1 with a kilogram."
              : "১ সের ≈ ৯৩৩ গ্রাম (০.৯৩৩১ কেজি)। ৪০ সের = ১ মন। গ্রামীণ বাজারে এখনো তেল, চিনি, ডাল সেরে বিক্রি হয় — ১ কেজির চেয়ে সামান্য কম, তাই ১:১ ধরা ভুল।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Tola / Bhori — gold & silver in Bangladesh" : "তোলা / ভরি — সোনা ও রূপায়"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 tola = 1 bhori = 11.664 g. Bangladesh's jewellery (Bajus) prices gold and silver per bhori. 1 bhori = 16 ana = 96 ratti. To convert a gold price per gram into per bhori, multiply by 11.664."
              : "১ তোলা = ১ ভরি = ১১.৬৬৪ গ্রাম। বাংলাদেশের জুয়েলারি (বাজুস) সোনা-রূপার দাম ভরিতে দেয়। ১ ভরি = ১৬ আনা = ৯৬ রতি। প্রতি গ্রামের দামকে ভরিতে রূপান্তর করতে ১১.৬৬৪ দিয়ে গুণ করুন।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Common BD weight conversions" : "প্রচলিত ওজন রূপান্তর"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Kg" : "কেজি"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Pounds" : "পাউন্ড"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Sher" : "সের"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Mon" : "মন"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Tola" : "তোলা"}</th>
                </tr>
              </thead>
              <tbody>
                {[1, 5, 10, 25, 37.324, 50, 75, 100, 500, 1000].map((kg) => (
                  <tr key={kg} className="border-t border-border">
                    <td className="p-2 tabular-nums">{num(kg)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertWeight(kg, "kg", "lb"), 3)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertWeight(kg, "kg", "sher"), 3)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertWeight(kg, "kg", "mon"), 4)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertWeight(kg, "kg", "tola"), 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Pounds → kg quick reference" : "পাউন্ড → কেজি দ্রুত রেফারেন্স"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Pounds" : "পাউন্ড"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Kg" : "কেজি"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Gram" : "গ্রাম"}</th>
                </tr>
              </thead>
              <tbody>
                {[1, 5, 10, 25, 50, 100, 150, 200, 500, 1000].map((lb) => (
                  <tr key={lb} className="border-t border-border">
                    <td className="p-2 tabular-nums">{num(lb)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertWeight(lb, "lb", "kg"), 3)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertWeight(lb, "lb", "g"), 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Shipping from Bangladesh — kg vs lb" : "বাংলাদেশ থেকে শিপিং — কেজি ও পাউন্ড"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "International couriers (DHL, FedEx, UPS, Aramex) bill by kg in Bangladesh, but US customers often think in pounds. A 5 kg parcel weighs 11.02 lb; a 23 kg airline checked-bag limit equals 50.7 lb. Always confirm whether courier rates use actual or volumetric weight."
              : "আন্তর্জাতিক কুরিয়ার (DHL, FedEx, UPS, Aramex) বাংলাদেশে কেজিতে বিল করে, কিন্তু মার্কিন গ্রাহকরা পাউন্ডে ভাবেন। ৫ কেজি প্যাকেজ = ১১.০২ পাউন্ড; এয়ারলাইন ২৩ কেজি চেক-ব্যাগ সীমা = ৫০.৭ পাউন্ড। অ্যাকচুয়াল না ভলিউমেট্রিক ওজনে বিল হবে — তা যাচাই করুন।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/bmi-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">BMI</Link>
            <Link to="/cm-to-feet" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">CM ↔ Feet</Link>
            <Link to="/feet-to-meter" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Feet ↔ Meter" : "ফুট ↔ মিটার"}</Link>
            <Link to="/land-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Land" : "জমি"}</Link>
          </div>
        </section>
      </article>
    </CalculatorLayout>
  );
}

function UnitSelect({
  value, onChange, lang,
}: { value: WeightUnitKey; onChange: (v: WeightUnitKey) => void; lang: Lang }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as WeightUnitKey)}
      className={cn(
        "w-full rounded-xl border border-border bg-card px-3 py-3 text-base font-medium focus:border-accent focus:outline-none",
        lang === "bn" && "font-bn",
      )}
    >
      {WEIGHT_UNITS.map((u) => (
        <option key={u.key} value={u.key}>
          {lang === "en" ? `${u.en} (${u.shortEn})` : `${u.bn} (${u.shortBn})`}
        </option>
      ))}
    </select>
  );
}
